import { Directive, ElementRef, OnDestroy, OnInit, inject, input, output } from '@angular/core';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Rend une boîte de dialogue custom accessible : rôle et labellisation ARIA, focus initial sur son
 * premier élément interactif, restitution du focus au déclencheur à la fermeture, et fermeture au
 * clavier via Échap. Le piégeage du focus (Tab/Shift+Tab) est délégué à pFocusTrap (PrimeNG), à
 * poser sur le même élément.
 */
@Directive({
  selector: '[appModalDialog]',
  host: {
    role: 'dialog',
    '[attr.aria-modal]': 'true',
    '[attr.aria-labelledby]': 'appModalDialog()',
    tabindex: '-1',
    '(keydown.escape)': 'onEscape($event)',
  },
})
export class ModalDialog implements OnInit, OnDestroy {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  /** id de l'élément (titre) qui décrit la boîte de dialogue, exposé en aria-labelledby. */
  readonly appModalDialog = input.required<string>();
  readonly closed = output<void>();

  private previouslyFocused: HTMLElement | null = null;

  ngOnInit(): void {
    this.previouslyFocused = document.activeElement as HTMLElement | null;
    queueMicrotask(() => {
      const focusable = this.elementRef.nativeElement.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      (focusable ?? this.elementRef.nativeElement).focus();
    });
  }

  ngOnDestroy(): void {
    this.previouslyFocused?.focus();
  }

  onEscape(event: Event): void {
    event.stopPropagation();
    this.closed.emit();
  }
}
